import CloseIcon from '@mui/icons-material/Close';
import cx from 'classnames';
import { deselectCard } from 'js/actions';
import DebouncedText from 'js/components/elements/editors/mui/DebouncedText';
import { CARD_TYPE_API_KEY, CARD_TYPE_PROJECT } from 'js/constants/cardTypes';
import {
  RESOURCE_TYPE_CREDENTIAL_GROUP,
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_FLOW,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import { canUpdateResource } from 'js/lib/flowValidationUtils';
import { ReactComponent as SplitIcon } from 'new/components/icons/fi-sr-split.svg';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { Button } from 'new/elements/Button';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import { IconButton } from 'new/elements/IconButton/IconButton';
import Typography from 'new/elements/Typography';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import { useUsers } from 'new/hooks/adapters/useUsers';
import React, { PropsWithChildren, ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { connectionMap } from './connectionMap';
import styles from './ResourceDetailHeader.module.scss';

interface ResourceDetailHeaderProps {
  readOnly: boolean;
  adminAccess: boolean;
  data: any;
  updateInfo: (id: number, fields: { [key: string]: string }) => void;
  onCloseDrawer?(): void;
}

const BoxInfo = ({ children, ...rest }) => {
  return (
    <div className={styles.boxInfo} {...rest}>
      <div className={styles.connectionBox}>{children}</div>
    </div>
  );
};

const chipStyles = {
  height: '32px',
  border: 'none',
  padding: 0,
};

type ConnectionType = {
  resourceType: string;
  data: any;
  iconSmall?: string | ReactNode;
};

const getPreposition = (data, resourceType) => {
  if (data?.node_type === 'query_prompt' || data?.flow_type === 'api_server') return null;
  return <Typography variant="bodySmall">{connectionMap[resourceType]?.preposition} </Typography>;
};

const getFromChipConnection = (data, connectionInfo) => {
  if (data?.node_type === 'query_prompt' || data?.flow_type === 'api_server') return null;

  return data?.dataSetType === 'shared' ? (
    <div className={styles.connectionBox}>
      <ConnectionTypeChip
        icon={<NexsetIcon className={styles.connectionIcon} />}
        isNexset
        style={chipStyles}
      />
    </div>
  ) : (
    <div className={styles.connectionBox}>
      <ConnectionTypeChip
        icon={connectionInfo?.iconSm || null}
        label={connectionInfo?.name}
        style={chipStyles}
      />
    </div>
  );
};

/**
 * [MainChip] [getPreposition] [getFromChipConnection]
 * e.g: DataFlow from Snowflake
 */

const Connection = ({ resourceType, data, iconSmall }: ConnectionType) => {
  const connectionInfo = useResourceMeta(resourceType, data.id, data?.data_credentials);

  const connectionArray = [RESOURCE_TYPE_SOURCE, RESOURCE_TYPE_SINK, RESOURCE_TYPE_CREDENTIALS];

  if (resourceType === RESOURCE_TYPE_DATA_SET) {
    const { is_detected: isDetected, shared: isShared, node_type } = data;
    if (isShared) {
      return (
        <div className={styles.boxInfo}>
          <div className={styles.connectionBox}>
            <ConnectionTypeChip
              icon={connectionMap[resourceType]?.icon}
              label={connectionMap[resourceType]?.label}
              style={chipStyles}
            />
          </div>
          <Typography variant="bodySmall">{connectionMap[resourceType]?.preposition}</Typography>
          <div className={styles.connectionBox}>
            <ConnectionTypeChip
              icon={connectionMap[resourceType]?.icon}
              label={connectionMap[resourceType]?.label}
              style={chipStyles}
            />
          </div>
        </div>
      );
    }

    if (node_type === 'splitter') {
      return (
        <div className={styles.boxInfo}>
          <div className={styles.connectionBox}>
            <ConnectionTypeChip icon={<SplitIcon />} label="Split Nexset" style={chipStyles} />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.boxInfo}>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionMap[resourceType]?.icon}
            label={connectionMap[resourceType]?.label}
            style={chipStyles}
          />
        </div>
        <Typography variant="bodySmall">{connectionMap[resourceType]?.preposition}</Typography>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionInfo?.iconSm || null}
            label={connectionInfo?.name}
            isNexset={!isDetected}
            style={chipStyles}
          />
        </div>
      </div>
    );
  } else if (connectionArray.includes(resourceType)) {
    return (
      <div className={styles.boxInfo}>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionMap[resourceType]?.icon}
            label={connectionMap[resourceType]?.label}
            style={chipStyles}
          />
        </div>
        <Typography variant="bodySmall">{connectionMap[resourceType]?.preposition}</Typography>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionInfo?.iconSm}
            label={connectionInfo?.name}
            style={chipStyles}
          />
        </div>
      </div>
    );
  } else if (resourceType === CARD_TYPE_API_KEY || resourceType === CARD_TYPE_PROJECT) {
    return (
      <div className={styles.boxInfo}>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionMap[resourceType]?.icon}
            label={connectionMap[resourceType]?.label}
            style={chipStyles}
          />
        </div>
      </div>
    );
  } else if (resourceType === RESOURCE_TYPE_FLOW) {
    return (
      <div className={styles.boxInfo}>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionMap[resourceType]?.icon}
            label={connectionMap[resourceType]?.label}
            style={chipStyles}
          />
        </div>

        {getPreposition(data, resourceType)}

        {getFromChipConnection(data, connectionInfo)}
      </div>
    );
  } else if (resourceType === RESOURCE_TYPE_CREDENTIAL_GROUP) {
    const primaryCredentialConnectionInfo = useResourceMeta(
      RESOURCE_TYPE_CREDENTIALS,
      data.primaryCredentialId,
    );
    return (
      <div className={styles.boxInfo}>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={connectionMap[resourceType]?.icon}
            label={connectionMap[resourceType]?.label}
            style={chipStyles}
          />
        </div>
        <Typography variant="bodySmall">{connectionMap[resourceType]?.preposition}</Typography>
        <div className={styles.connectionBox}>
          <ConnectionTypeChip
            icon={primaryCredentialConnectionInfo?.iconSm}
            label={primaryCredentialConnectionInfo?.name}
            style={chipStyles}
          />
        </div>
      </div>
    );
  }
  return (
    <div className={styles.boxInfo}>
      <div className={styles.connectionBox}>
        <ConnectionTypeChip
          icon={connectionInfo?.iconSm || null}
          label={connectionInfo?.name}
          style={chipStyles}
        />
      </div>
    </div>
  );
};

const ViewFlowButton = (props) => {
  return (
    <Button
      {...props}
      size="small"
      variant="outlined"
      color="primary"
      className={cx(styles.viewDataFlow, props.className)}
    >
      View Data Flow
    </Button>
  );
};

const TopContent = (props: PropsWithChildren<{ onCloseDrawer?(): void }>) => {
  const { children } = props;
  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    dispatch(deselectCard());
    props.onCloseDrawer?.();
  }, [props?.onCloseDrawer]);

  return (
    <div className={styles.topContent}>
      <div className={styles.topContentChildren}>{children}</div>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </div>
  );
};

const ConnectionBox = (props: PropsWithChildren<any>) => {
  return <div className={styles.connectionBox}>{props.children}</div>;
};

TopContent.BoxInfo = BoxInfo;
TopContent.ConnectionBox = ConnectionBox;
TopContent.ViewFlowButton = ViewFlowButton;
TopContent.Connection = Connection;
TopContent.type = 'TopContent';

const isTopContent = (c: any) => c?.type?.type === 'TopContent';

const ResourceDetailHeader = (props: PropsWithChildren<ResourceDetailHeaderProps>) => {
  const { children, readOnly, data, updateInfo, onCloseDrawer } = props;
  const members = useUsers(true);
  const aclQueryMode = useSelector((state: any) => state.app.listing.aclQueryMode);

  const topContent = (() => {
    if (Array.isArray(children)) {
      const topContentNode = children.find(isTopContent);
      if (topContentNode) {
        return React.cloneElement(topContentNode as any, { onCloseDrawer });
      }
    }

    return children && isTopContent(children)
      ? React.cloneElement(children as any, { onCloseDrawer })
      : undefined;
  })();

  const childrenToRender = (() => {
    if (Array.isArray(children)) {
      return children.filter((c) => c?.type?.type !== 'TopContent');
    }
    return children?.type?.type === 'TopContent' ? undefined : children;
  })();

  const handleInputChange = (field) => (value) => {
    updateInfo(data.id, { [field]: value });
  };

  // check if this is a flow and if it can be updated
  const isFlow = data?.type === RESOURCE_TYPE_FLOW || data?.flow_node_id;
  const updateValidation = isFlow ? canUpdateResource(data) : { canActivate: true };
  const isDisabledByArchivalStatus = !updateValidation.canActivate;

  const nameField = readOnly ? (
    <div className={styles.title}>{data.name || ''}</div>
  ) : (
    <DebouncedText
      disabled={readOnly || isDisabledByArchivalStatus}
      inputTheme="lightHover"
      placeholder="Name"
      value={data.name || ''}
      rootStyle={debouncedInputRootStyle}
      onChange={handleInputChange('name')}
    />
  );

  const descField = readOnly ? (
    <div className={styles.subtitle}>{data.description || ''}</div>
  ) : (
    <DebouncedText
      disabled={readOnly || isDisabledByArchivalStatus}
      inputTheme="lightHover"
      rootStyle={{ marginTop: '3px', ...debouncedInputRootStyle }}
      placeholder="Add description..."
      value={data.description || ''}
      onChange={handleInputChange('description')}
    />
  );

  const owner = members[data.owner_id];

  const readOnlyLabel = shouldDisplayOwnershipLabel(aclQueryMode) ? (
    <div className={styles.subtitleLabel}>
      <b>
        Access Level: {ownershipLabel(data)}
        {!isOwner(data) ? ` | Owned by: ` : ``}
      </b>
      {!isOwner(data) ? owner?.email : ''}
    </div>
  ) : null;

  return (
    <>
      {topContent}
      <div className={styles.content}>
        <div>
          {nameField}
          {descField}
          {readOnlyLabel}
        </div>
        {childrenToRender}
      </div>
    </>
  );
};

const debouncedInputRootStyle = {
  paddingLeft: 4,
  borderRadius: 4,
  border: '1px solid var(--color-greyscale-100)',
};

ResourceDetailHeader.TopContent = TopContent;

export default ResourceDetailHeader;
